import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_URL } from '../../../../Constant';
import { showLoading } from 'react-global-loading';
import { toast, ToastContainer } from 'react-toastify';
import messages from '../../../../assets/Messages.json';
import 'react-toastify/dist/ReactToastify.css';
import useASIItemMaster from '../../../../hooks/useASIItemMaster';
import Remarks from './Remarks';
import Remarks__list from './Remarks__list';
import { markPageAsSaved, PAGE_NAMES, refreshBalanceSheetValidation } from '../../../../Components/BalanceSheet/BalanceSheetHeader';
import { filterRemarksList } from '../../../../Custom/Custom';

// API configuration exactly like other pages - using dynamic token with multiple fallbacks
const getAuthConfig = () => {
  // Try multiple possible token keys
  let token = localStorage.getItem('token') ||
    localStorage.getItem('jwt') ||
    localStorage.getItem('accessToken') ||
    localStorage.getItem('authToken') ||
    localStorage.getItem('userToken');

  if (!token) {
    // Try to get from sessionStorage as well
    token = sessionStorage.getItem('token') ||
      sessionStorage.getItem('jwt') ||
      sessionStorage.getItem('accessToken');
  }

  if (!token) {
    console.error('No token found in localStorage or sessionStorage');
    console.log('Available localStorage keys:', Object.keys(localStorage));
    console.log('Available sessionStorage keys:', Object.keys(sessionStorage));
    return null;
  }

  console.log('Token found:', token.substring(0, 20) + '...');

  return {
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
      'Accept': 'application/json',
      'Cache-Control': 'no-cache'
    }
  };
};

function ProductsAndByProducts() {
  const navigate = useNavigate();

  // Use the custom hook for ASI Item Master data
  const {
    itemMasterData,
    filteredItems,
    isLoading: loadingItemMaster,
    debouncedSearch,
    loadMoreData,
    hasMoreData,
    isLoadingMore,
  } = useASIItemMaster();

  // State for header data
  const [headerData, setHeaderData] = useState({
    despatchSlNo: '',
    permanentSlNo: '',
    companyName: '',
    dslNo: '',
    surveyYear: ''
  });

  const [itemData, setItemData] = useState({});
  const [checkedItems, setCheckedItems] = useState([]);
  const [buttonStatus, setButtonStatus] = useState('');
  const [originalValues, setOriginalValues] = useState({});

  // State for Remarks section
  const [remarksData, setRemarksData] = useState({
    remarksBlock: '',
    remarksItems: '',
    remarks: ''
  });
  const [remarksList, setRemarksList] = useState([]);

  const [totalProfitandLossAmount, setTotalProfitandLossAmount] = useState({
    SL_No: '',
    Description: '',
    Amount: ''
  });

  // State for managing dropdown visibility for each row and field
  const [dropdownStates, setDropdownStates] = useState({});

  // Ref: ignore outside-click for a short window after opening (fixes production build + touch devices)
  const dropdownOpenedAtRef = useRef(0);
  const DROPDOWN_OPEN_GUARD_MS = 300;

  // Get already selected item codes from all rows (first 10 user editable rows)
  // This will be used to filter out already selected items from dropdown
  const selectedItemCodes = useMemo(() => {
    const selected = {};
    for (let i = 0; i < 10; i++) {
      const item = itemData[i];
      if (item && item.itemCode && item.itemCode.toString().trim() !== '') {
        selected[i] = item.itemCode.toString().trim();
      }
    }
    return selected;
  }, [itemData]);

  // Function to get filtered items for a specific row (excluding already selected items in other rows)
  const getFilteredItemsForRow = useCallback((rowIndex, items) => {
    return items.filter(dropdownItem => {
      const dropdownItemCode = dropdownItem.ASICC_CODE?.toString().trim();
      // Check if this item is selected in any other row (not the current row)
      for (const [index, code] of Object.entries(selectedItemCodes)) {
        if (parseInt(index) !== rowIndex && code === dropdownItemCode) {
          return false; // Item is selected in another row, filter it out
        }
      }
      return true;
    });
  }, [selectedItemCodes]);

  // Function to get filtered Product Descriptions for a specific row (excluding already selected items in other rows)
  const getFilteredProductDescsForRow = useCallback((rowIndex, items) => {
    return items.filter(dropdownItem => {
      const dropdownItemName = dropdownItem.ITEM_NAME?.toString().trim();
      // Check if this product description is selected in any other row (not the current row)
      for (let i = 0; i < 10; i++) {
        if (i !== rowIndex) {
          const item = itemData[i];
          if (item && item.productDesc && item.productDesc.toString().trim() === dropdownItemName) {
            return false; // Product description is selected in another row, filter it out
          }
        }
      }
      return true;
    });
  }, [itemData]);

  // Get selected Product Descriptions from table for Remarks Items dropdown
  // Include user selected items from first 10 rows + row 11 (Other products/by-products)
  const selectedItemDescriptions = useMemo(() => {
    const descriptions = [];

    // Add user selected items from first 10 rows
    for (let i = 0; i < 10; i++) {
      const item = itemData[i];
      if (item && item.productDesc && item.productDesc.toString().trim() !== '') {
        descriptions.push(item.productDesc.toString().trim());
      }
    }

    // Add row 11 (Other products/by-products) - should always be shown
    const row11Item = itemData[10];
    if (row11Item && row11Item.productDesc && row11Item.productDesc.toString().trim() !== '') {
      descriptions.push(row11Item.productDesc.toString().trim());
    }

    return descriptions;
  }, [itemData]);

  // Function to open dropdown for specific row and field (used on focus/click - avoids double toggle)
  const openDropdown = (rowIndex, fieldType) => {
    dropdownOpenedAtRef.current = Date.now(); // Ignore outside events for a short window (fixes production + touch)
    setDropdownStates({ [`${rowIndex}-${fieldType}`]: true });
  };

  // Function to close dropdown for specific row and field
  const closeDropdown = (rowIndex, fieldType) => {
    setDropdownStates(prev => ({
      ...prev,
      [`${rowIndex}-${fieldType}`]: false
    }));
  };

  // Function to close all dropdowns
  const closeAllDropdowns = () => {
    setDropdownStates({});
  };

  // Handle search input change
  const handleSearchChange = (value) => {
    debouncedSearch(value);
  };

  // Initialize table with default data
  const initializeTableData = () => {
    const initialData = {};

    // First 11 rows (user editable)
    for (let i = 0; i < 12; i++) {
      initialData[i] = {
        id: i,
        dslNo: headerData.despatchSlNo,
        surveyYear: '2023-24',
        productDesc: '',
        itemCode: '',
        unitOfQty: '',
        qtyManufactured: '',
        qtySold: '',
        grossSaleValue: '',
        gst: '',
        exciseDuty: '',
        otherExpenses: '',
        subsidy: '',
        statusFlag: '',
        saveOrderBy: 1,
        itemCodeClass: 'textBox_back_color_white'
      };
    }

    // Set specific data for last row (index 11) - disable and set '0' values
    initialData[11] = {
      ...initialData[11],
      productDesc: 'Values from PL Account for information',
      itemCode: '',
      unitOfQty: '',
      qtyManufactured: '',
      qtySold: '',
      grossSaleValue: '0',
      gst: '0',
      exciseDuty: '0',
      otherExpenses: '0',
      subsidy: '0'
    };

    // Add row 13 data for Share (%) of products/by-products directly exported
    initialData[12] = {
      id: 12,
      dslNo: headerData.despatchSlNo,
      surveyYear: '2023-24',
      shareExported: '0'
    };

    setItemData(initialData);
    dataLoadedRef.current = true; // Mark data as loaded
    console.log('Table data initialized, dataLoadedRef set to true');
  };

  // Helper function to calculate Per Unit Net Sale Value
  // Formula: (col7 - (col8 + col9 + col10 - col11)) / col6
  // Which is: (grossSaleValue - (gst + exciseDuty + otherExpenses - subsidy)) / qtySold
  const calculatePerUnitNetSaleValue = (item) => {
    const qtySold = parseFloat(item.qtySold) || 0;
    const grossSaleValue = parseFloat(item.grossSaleValue) || 0;
    const gst = parseFloat(item.gst) || 0;
    const exciseDuty = parseFloat(item.exciseDuty) || 0;
    const otherExpenses = parseFloat(item.otherExpenses) || 0;
    const subsidy = parseFloat(item.subsidy) || 0;

    if (qtySold > 0) {
      const numerator = grossSaleValue - (gst + exciseDuty + otherExpenses - subsidy);
      const perUnitNetSaleValue = numerator / qtySold;
      // Round to 2 decimal places
      return perUnitNetSaleValue.toFixed(2);
    }
    return '';
  };

  const handleInputChange = (index, field, value) => {
    const newFormData = { ...itemData };
    let validatedValue = value;

    // Standard validation for numeric fields (same as FixedAssets.jsx)
    if (['qtyManufactured', 'qtySold', 'grossSaleValue', 'gst', 'exciseDuty', 'otherExpenses', 'subsidy'].includes(field)) {
      // Allow empty string, numbers, and decimal points
      if (value === '' || value === '0' || /^\d*\.?\d*$/.test(value)) {
        // Limit to 2 decimal places
        if (value.includes('.')) {
          const parts = value.split('.');
          if (parts[1] && parts[1].length > 2) {
            validatedValue = parts[0] + '.' + parts[1].substring(0, 2);
          }
        }

        // Limit total length to 15 characters (including decimal point)
        if (validatedValue.length > 15) {
          validatedValue = validatedValue.substring(0, 15);
        }
      } else {
        // If invalid characters, keep previous value
        return;
      }
    }

    // Validation for percentage field (shareExported)
    if (field === 'shareExported') {
      // Allow empty string, numbers, and decimal point
      if (value === '' || value === '0' || /^\d*\.?\d*$/.test(value)) {
        // Limit to 2 decimal places
        if (value.includes('.')) {
          const parts = value.split('.');
          if (parts[1] && parts[1].length > 2) {
            validatedValue = parts[0] + '.' + parts[1].substring(0, 2);
          }
        }

        // Limit total length to 6 characters (percentage: 0-100, max 2 decimals)
        if (validatedValue.length > 6) {
          validatedValue = validatedValue.substring(0, 6);
        }

        // Validate percentage range (0-100) after validation
        if (validatedValue !== '' && validatedValue !== '.') {
          const numValue = parseFloat(validatedValue);
          if (!isNaN(numValue) && (numValue < 0 || numValue > 100)) {
            toast.error('Percentage must be between 0 and 100', messages.COLOR_RED);
            return;
          }
        }
      } else {
        // If invalid characters, keep previous value
        return;
      }
    }

    // Note: Quantity sold can be greater than quantity manufactured due to inventory
    // This is allowed as per business requirements - no validation needed

    // Validation for item code
    if (field === 'itemCode') {
      // Allow only numbers and empty string
      if (value === '' || /^\d*$/.test(value)) {
        // Limit to 7 digits
        if (value.length > 7) {
          validatedValue = value.substring(0, 7);
        }
      } else {
        // If invalid characters, keep previous value
        return;
      }
    }

    // For unitOfQty - allow any text but limit length
    if (field === 'unitOfQty') {
      // Limit to 19 characters as per maxLength
      if (value.length > 19) {
        validatedValue = value.substring(0, 19);
      }
    }

    newFormData[index][field] = validatedValue;

    // Auto-calculate Per Unit Net Sale Value when related fields change
    if (['qtySold', 'grossSaleValue', 'gst', 'exciseDuty', 'otherExpenses', 'subsidy'].includes(field)) {
      newFormData[index].perUnitNetSaleValue = calculatePerUnitNetSaleValue(newFormData[index]);
    }

    setItemData(newFormData);
  };

  // Handle dropdown change for Product Description - using API data
  const handleProductDescriptionChange = (index, value) => {
    const newFormData = { ...itemData };

    if (value) {
      const selectedItem = itemMasterData.find(item => item.ITEM_NAME === value);
      if (selectedItem) {
        newFormData[index] = {
          ...newFormData[index],
          productDesc: selectedItem.ITEM_NAME,
          itemCode: selectedItem.ASICC_CODE,
          unitOfQty: selectedItem.UNIT_NAME || ''
        };
      }
    } else {
      newFormData[index] = {
        ...newFormData[index],
        productDesc: '',
        itemCode: '',
        unitOfQty: ''
      };
    }

    setItemData(newFormData);
  };

  // Handle dropdown change for Item Code - using API data
  const handleItemCodeChange = async (index, value) => {
    const newFormData = { ...itemData };

    if (value) {
      const selectedItem = itemMasterData.find(item => item.ASICC_CODE === value);
      if (selectedItem) {
        newFormData[index] = {
          ...newFormData[index],
          productDesc: selectedItem.ITEM_NAME,
          itemCode: selectedItem.ASICC_CODE,
          unitOfQty: selectedItem.UNIT_NAME || ''
        };
      }
    } else {
      newFormData[index] = {
        ...newFormData[index],
        productDesc: '',
        itemCode: '',
        unitOfQty: ''
      };
    }

    setItemData(newFormData);
  };

  // Handle reset function to clear all fields
  const handleReset = () => {
    const newItemData = { ...itemData };
    Object.keys(newItemData).forEach(key => {
      if (key == 12) {
        // Special handling for row 13 (shareExported)
        newItemData[key] = {
          ...newItemData[key],
          shareExported: '0'
        };
      } else {
        newItemData[key] = {
          ...newItemData[key],
          productDesc: '',
          itemCode: '',
          unitOfQty: '',
          qtyManufactured: '',
          qtySold: '',
          grossSaleValue: '',
          gst: '',
          exciseDuty: '',
          otherExpenses: '',
          subsidy: ''
        };
      }
    });
    setItemData(newItemData);
    setCheckedItems([]);
    closeAllDropdowns();
  };


  // Validation functions
  const validateNumericField = (value, fieldName, min = 0, max = 999999999) => {
    if (value === '' || value === null || value === undefined) return true; // Allow empty

    const numValue = parseFloat(value);
    if (isNaN(numValue)) {
      toast.error(`${fieldName} must be a valid number`, messages.COLOR_RED);
      return false;
    }

    if (numValue < min) {
      toast.error(`${fieldName} cannot be less than ${min}`, messages.COLOR_RED);
      return false;
    }

    if (numValue > max) {
      toast.error(`${fieldName} cannot be greater than ${max}`, messages.COLOR_RED);
      return false;
    }

    return true;
  };

  const validateRequiredField = (value, fieldName) => {
    if (!value || value.toString().trim() === '') {
      toast.error(`${fieldName} is required`, messages.COLOR_RED);
      return false;
    }
    return true;
  };

  const validateItemCode = (itemCode) => {
    if (!itemCode || itemCode.toString().trim() === '') return true; // Allow empty

    const codeStr = itemCode.toString().trim();
    if (codeStr.length < 7) {
      toast.error('Item Code must be at least 7 digits', messages.COLOR_RED);
      return false;
    }

    if (!/^\d{7,}$/.test(codeStr)) {
      toast.error('Item Code must contain only numbers', messages.COLOR_RED);
      return false;
    }

    return true;
  };

  const validateQuantitySoldVsManufactured = (manufactured, sold) => {
    if (!manufactured || !sold) return true; // Skip if either is empty

    const manufacturedNum = parseFloat(manufactured);
    const soldNum = parseFloat(sold);

    if (isNaN(manufacturedNum) || isNaN(soldNum)) return true; // Skip if invalid numbers

    // Allow quantity sold to be greater than manufactured as it can include previous year's stock
    // This is a business rule - sold quantity can exceed manufactured quantity due to inventory
    // No validation error needed for this case

    return true;
  };

  const validatePercentageField = (value, fieldName) => {
    if (value === '' || value === null || value === undefined) return true; // Allow empty

    const numValue = parseFloat(value);
    if (isNaN(numValue)) {
      toast.error(`${fieldName} must be a valid percentage`, messages.COLOR_RED);
      return false;
    }

    if (numValue < 0) {
      toast.error(`${fieldName} cannot be less than 0%`, messages.COLOR_RED);
      return false;
    }

    if (numValue > 100) {
      toast.error(`${fieldName} cannot be greater than 100%`, messages.COLOR_RED);
      return false;
    }

    return true;
  };

  const validateFormData = () => {
    let isValid = true;

    // Validate DSL No and Survey Year
    if (!validateRequiredField(headerData.dslNo, 'DSL No.')) {
      isValid = false;
    }

    if (!validateRequiredField(headerData.surveyYear, 'Survey Year')) {
      isValid = false;
    }

    // Validate DSL No format
    if (headerData.dslNo && !/^\d{6}$/.test(headerData.dslNo)) {
      toast.error('DSL No. must be exactly 6 digits', messages.COLOR_RED);
      isValid = false;
    }

    // Validate each row with data
    for (let i = 0; i < 10; i++) {
      const item = itemData[i];
      if (!item) continue;

      // Skip validation if row is completely empty
      const hasData = item.productDesc || item.itemCode || item.qtyManufactured ||
        item.qtySold || item.grossSaleValue || item.gst ||
        item.exciseDuty || item.otherExpenses;

      if (!hasData) continue;

      // Validate required fields for rows with data
      if (!validateRequiredField(item.productDesc, `Product Description (Row ${i + 1})`)) {
        isValid = false;
      }

      if (!validateRequiredField(item.itemCode, `Item Code (Row ${i + 1})`)) {
        isValid = false;
      }

      if (!validateRequiredField(item.unitOfQty, `Unit of Quantity (Row ${i + 1})`)) {
        isValid = false;
      }

      // Validate item code format
      if (item.itemCode && !validateItemCode(item.itemCode)) {
        isValid = false;
      }

      // Validate numeric fields
      if (item.qtyManufactured && !validateNumericField(item.qtyManufactured, `Quantity Manufactured (Row ${i + 1})`, 0, 999999999)) {
        isValid = false;
      }

      if (item.qtySold && !validateNumericField(item.qtySold, `Quantity Sold (Row ${i + 1})`, 0, 999999999)) {
        isValid = false;
      }

      if (item.grossSaleValue && !validateNumericField(item.grossSaleValue, `Gross Sale Value (Row ${i + 1})`, 0, 999999999999)) {
        isValid = false;
      }

      if (item.gst && !validateNumericField(item.gst, `GST (Row ${i + 1})`, 0, 999999999999)) {
        isValid = false;
      }

      if (item.exciseDuty && !validateNumericField(item.exciseDuty, `Excise Duty (Row ${i + 1})`, 0, 999999999999)) {
        isValid = false;
      }

      if (item.otherExpenses && !validateNumericField(item.otherExpenses, `Other Expenses (Row ${i + 1})`, 0, 999999999999)) {
        isValid = false;
      }

      if (item.subsidy && !validateNumericField(item.subsidy, `Subsidy (Row ${i + 1})`, 0, 999999999999)) {
        isValid = false;
      }

      // Validate business logic - Quantity sold can be greater than manufactured due to inventory
      // This validation is now allowed as per business requirements
      // if (item.qtyManufactured && item.qtySold && !validateQuantitySoldVsManufactured(item.qtyManufactured, item.qtySold)) {
      //   isValid = false;
      // }
    }

    // Validate row 13 - Share (%) of products/by-products directly exported
    const shareExportedItem = itemData[12];
    if (shareExportedItem && shareExportedItem.shareExported !== undefined) {
      if (!validatePercentageField(shareExportedItem.shareExported, 'Share (%) of products/by-products directly exported')) {
        isValid = false;
      }
    }

    return isValid;
  };

  // Function to save data to API
  const handleSaveToAPI = async () => {
    // Validate form data first
    if (!validateFormData()) {
      return false;
    }

    showLoading(true);

    try {
      const currentConfig = getAuthConfig();
      if (!currentConfig) {
        toast.error('Authentication token not found. Please login again.', messages.COLOR_RED);
        showLoading(false);
        return false;
      }

      // Prepare data for API - only first 10 rows (user editable)
      const dataToSave = [];
      
      // Get Share Exported percentage value from row 12
      const shareExportedValue = itemData[12]?.shareExported ? parseFloat(itemData[12].shareExported) : 0;

      // Include rows 1-10 (0-9) and "Other products/by-products" row (index 10)
      for (let i = 0; i < 11; i++) {
        const item = itemData[i];
        if (item && (item.productDesc || item.itemCode || item.qtyManufactured || item.qtySold || item.grossSaleValue)) {
          // Calculate distributive_expenses_total
          const exciseDuty = item.exciseDuty ? parseFloat(item.exciseDuty) : 0;
          const gst = item.gst ? parseFloat(item.gst) : 0;
          const otherExpenses = item.otherExpenses ? parseFloat(item.otherExpenses) : 0;
          const distributiveExpensesTotal = exciseDuty + gst + otherExpenses;

          // Get perunit_net_sale (auto-calculated value)
          const perUnitNetSale = item.perUnitNetSaleValue ? parseFloat(item.perUnitNetSaleValue) : 0;

          const payload = {
            dsl_no: headerData.dslNo,
            survey_year: headerData.surveyYear.split('-')[0] || '2022', // Extract year from format like "2022-23"
            item_desc: item.productDesc || '',
            item_code: item.itemCode || '',
            other_item: '', // New field
            unit_of_qty: item.unitOfQty || '',
            qty_manufactured: item.qtyManufactured ? parseFloat(item.qtyManufactured) : 0,
            qty_sold: item.qtySold ? parseFloat(item.qtySold) : 0,
            gross_sale_value: item.grossSaleValue ? parseFloat(item.grossSaleValue) : 0,
            goods_service_tax: gst, // Changed from distributive_expenses_sales_tax_vat
            excise_duty: exciseDuty, // Changed from distributive_expenses_excise_duty
            other_distributive_expanse: otherExpenses, // Changed from distributive_expenses_others
            subsidy: item.subsidy ? parseFloat(item.subsidy) : 0,
            perunit_net_sale: perUnitNetSale, // Changed from perunit_netsale_value
            distributive_expenses_total: distributiveExpensesTotal
          };

          dataToSave.push(payload);
        }
      }

      if (dataToSave.length === 0) {
        toast.error('No data to save. Please fill at least one row.', messages.COLOR_RED);
        showLoading(false);
        return false;
      }

      // Prepare remarks array
      const remarksArray = [];
      if (remarksData.remarksBlock || remarksData.remarksItems || remarksData.remarks) {
        remarksArray.push({
          dslNo: headerData.dslNo,
          surveyYear: headerData.surveyYear.split('-')[0] || '2022',
          block: 'J',
          columnsName: remarksData.remarksItems || '',
          Items: remarksData.remarksBlock || '',
          remarks: remarksData.remarks || ''
        });
      }

      // Structure payload with data and remarks arrays
      const finalPayload = {
        data: dataToSave,
        remarks: remarksArray
      };

      console.log('Saving data to API:', finalPayload);

      const response = await axios.post(`${API_URL}api/input-items-directlyItems/insertInputJItems`, finalPayload, currentConfig);

      // Check response
      if (response.data && response.data.status) {
        toast.success(`Successfully saved ${dataToSave.length} items to database`, messages.COLOR_RED);
        
        // Mark page as saved for green header
        markPageAsSaved(PAGE_NAMES.PRODUCTS_BY_PRODUCTS);
        
        return true;
      } else {
        toast.error('Failed to save data. Please check the data and try again.', messages.COLOR_RED);
        return false;
      }

    } catch (error) {
      console.error('API Error Details:', error);
      console.error('Error Response:', error.response);
      console.error('Error Status:', error.response?.status);
      console.error('Error Data:', error.response?.data);

      if (error.response?.status === 403) {
        toast.error('Access denied (403). Please check your permissions or contact administrator.', messages.COLOR_RED);
      } else if (error.response?.status === 401) {
        toast.error('Unauthorized (401). Please login again.', messages.COLOR_RED);
      } else if (error.response?.status === 404) {
        toast.error('API endpoint not found (404). Please check the URL.', messages.COLOR_RED);
      } else if (error.response?.status >= 500) {
        toast.error('Server error. Please try again later.', messages.COLOR_RED);
      } else {
        toast.error(`Error ${error.response?.status || 'Unknown'}: ${error.message || 'An error occurred'}`, messages.COLOR_RED);
      }
      return false;
    } finally {
      showLoading(false);
    }
  };

  // Use ref to track if data has been loaded to prevent multiple API calls
  const dataLoadedRef = useRef(false);
  const isLoadingRef = useRef(false);
  const apiCallMadeRef = useRef(false);

  // Function to fetch existing data from API
  const fetchExistingData = useCallback(async (dslNo, surveyYear) => {
    if (!dslNo || !surveyYear) {
      console.log('DSL No or Survey Year not available for fetching data');
      dataLoadedRef.current = true;
      return;
    }

    showLoading(true);
    try {
      const currentConfig = getAuthConfig();
      if (!currentConfig) {
        console.error('Authentication token not found for fetching data');
        showLoading(false);
        dataLoadedRef.current = true;
        return;
      }

      const payload = {
        dsl_no: dslNo,
        survey_year: surveyYear.split('-')[0] // Extract year from format like "2022-23" -> "2022"
      };

      const response = await axios.post(`${API_URL}api/input-items-directlyItems/getInputJItems`, payload, currentConfig);

      if (response.data && response.data.status && Array.isArray(response.data.data)) {
        try {
          const apiData = response.data.data;

          // Empty data - initialize default form
          if (apiData.length === 0) {
            initializeTableData();
            dataLoadedRef.current = true;
            showLoading(false);
            return;
          }

          // Helper: Convert null/undefined to empty string
          const safeString = (val) => (val == null ? '' : String(val));

          // Initialize mapped data structure
          const mappedData = {};

          // Separate user data and system data
          const userData = apiData.filter(item => !['9921100', '9995000', '9999900'].includes(item.item_code));
          const systemData = apiData.filter(item => ['9921100', '9995000', '9999900'].includes(item.item_code));

          // Map user data to rows 0-9
          userData.slice(0, 10).forEach((apiItem, index) => {
            const mappedItem = {
              id: index,
              dslNo: safeString(apiItem.dsl_no),
              surveyYear: safeString(apiItem.survey_year),
              productDesc: safeString(apiItem.item_desc || apiItem.product_byproduct_desc),
              itemCode: safeString(apiItem.item_code),
              unitOfQty: safeString(apiItem.unit_of_qty),
              qtyManufactured: safeString(apiItem.qty_manufactured),
              qtySold: safeString(apiItem.qty_sold),
              grossSaleValue: safeString(apiItem.gross_sale_value),
              gst: safeString(apiItem.goods_service_tax || apiItem.distributive_expenses_sales_tax_vat),
              exciseDuty: safeString(apiItem.excise_duty || apiItem.distributive_expenses_excise_duty),
              otherExpenses: safeString(apiItem.other_distributive_expanse || apiItem.distributive_expenses_others),
              subsidy: safeString(apiItem.subsidy || ''),
              statusFlag: safeString(apiItem.status_flag),
              saveOrderBy: index + 1,
              itemCodeClass: 'textBox_back_color_white'
            };
            // Use API value if available, otherwise calculate
            if (apiItem.perunit_net_sale !== null && apiItem.perunit_net_sale !== undefined) {
              mappedItem.perUnitNetSaleValue = safeString(apiItem.perunit_net_sale);
            } else {
              mappedItem.perUnitNetSaleValue = calculatePerUnitNetSaleValue(mappedItem);
            }
            mappedData[index] = mappedItem;
          });

          // Fill remaining user rows (if less than 10 items)
          for (let i = userData.length; i < 10; i++) {
            mappedData[i] = {
              id: i,
              dslNo: dslNo,
              surveyYear: surveyYear,
              productDesc: '',
              itemCode: '',
              unitOfQty: '',
              qtyManufactured: '',
              qtySold: '',
              grossSaleValue: '',
              gst: '',
              exciseDuty: '',
              otherExpenses: '',
              subsidy: '',
              statusFlag: '',
              saveOrderBy: i + 1,
              itemCodeClass: 'textBox_back_color_white'
            };
          }

          // Extract Share Exported percentage (exfactory_value_qty) from any API row
          const shareExportedFromApi = (() => {
            const found = apiData.find(row => row?.exfactory_value_qty !== null && row?.exfactory_value_qty !== undefined);
            return found ? safeString(found.exfactory_value_qty) : '';
          })();

          // Map system data to rows 10-11
          const specialRows = [
            { code: '9921100', desc: 'Other products/by-products' },
            { code: '', desc: 'Values from PL Account for information' }
          ];

          specialRows.forEach((specialRow, idx) => {
            const rowIndex = 10 + idx;
            const systemItem = systemData.find(item => item.item_code === specialRow.code);

            const mappedItem = {
              id: rowIndex,
              dslNo: dslNo,
              surveyYear: surveyYear,
              productDesc: systemItem ? safeString(systemItem.item_desc || systemItem.product_byproduct_desc) : specialRow.desc,
              itemCode: systemItem ? safeString(systemItem.item_code) : (specialRow.code || ''),
              unitOfQty: systemItem ? safeString(systemItem.unit_of_qty) : '',
              qtyManufactured: systemItem ? safeString(systemItem.qty_manufactured) : '',
              qtySold: systemItem ? safeString(systemItem.qty_sold) : '',
              grossSaleValue: systemItem ? safeString(systemItem.gross_sale_value) : (idx === 1 ? '0' : ''),
              gst: systemItem ? safeString(systemItem.goods_service_tax || systemItem.distributive_expenses_sales_tax_vat) : (idx === 1 ? '0' : ''),
              exciseDuty: systemItem ? safeString(systemItem.excise_duty || systemItem.distributive_expenses_excise_duty) : (idx === 1 ? '0' : ''),
              otherExpenses: systemItem ? safeString(systemItem.other_distributive_expanse || systemItem.distributive_expenses_others) : (idx === 1 ? '0' : ''),
              subsidy: systemItem ? safeString(systemItem.subsidy || '') : (idx === 1 ? '0' : ''),
              statusFlag: '',
              saveOrderBy: rowIndex + 1,
              itemCodeClass: 'textBox_back_color_white'
            };
            // Use API value if available, otherwise calculate
            if (systemItem && systemItem.perunit_net_sale !== null && systemItem.perunit_net_sale !== undefined) {
              mappedItem.perUnitNetSaleValue = safeString(systemItem.perunit_net_sale);
            } else {
              mappedItem.perUnitNetSaleValue = calculatePerUnitNetSaleValue(mappedItem);
            }
            mappedData[rowIndex] = mappedItem;
          });

          // Add row 12 for Share Exported percentage
          mappedData[12] = {
            id: 12,
            dslNo: dslNo,
            surveyYear: surveyYear,
            shareExported: shareExportedFromApi || '0'
          };

          // Update state with new mapped data
          setItemData(mappedData);

          // Map remarks data from API response (similar to InputItemsImported.jsx)
          let mappedRemarksList = [];
          if (response.data.remarks && Array.isArray(response.data.remarks)) {
            mappedRemarksList = response.data.remarks.map((remark, index) => {
              // Extract block letter from "Block J" format (e.g., "Block J" -> "J")
              let blockLetter = '';
              if (remark.block) {
                blockLetter = remark.block.replace(/^Block\s+/i, '').trim();
              }

              return {
                slNo: index + 1,
                blocks: blockLetter || remark.block || '',
                columns: remark.columns_name || remark.columnsName || '',
                items: remark.items || '',
                remarks: remark.remarks || '',
                remarksCreatedOn: remark.created_date || remark.createdDate || remark.updated_date || '',
                remarksBy: remark.updated_by || remark.updatedBy || remark.created_by || '',
                role: remark.updated_by || remark.updatedBy || ''
              };
            });
            setRemarksList(mappedRemarksList);
          } else {
            setRemarksList([]);
          }

          // Set default remarksData - Block to "ALL" by default to show all remarks
          setRemarksData({
            remarksBlock: (mappedRemarksList.length > 0) ? 'ALL' : 'SELECT_ITEM',
            remarksItems: '',
            remarks: ''
          });

          // Map totalProfitandLossAmount from API response
          if (response.data.totalProfitandLossAmount) {
            const profitLossData = response.data.totalProfitandLossAmount;
            setTotalProfitandLossAmount({
              SL_No: profitLossData.SL_No !== undefined ? profitLossData.SL_No : (profitLossData.sl_No !== undefined ? profitLossData.sl_No : ''),
              Description: profitLossData.Description || profitLossData.description || '',
              Amount: profitLossData.Amount !== undefined && profitLossData.Amount !== null ? profitLossData.Amount.toString() : (profitLossData.amount !== undefined && profitLossData.amount !== null ? profitLossData.amount.toString() : '')
            });
          } else {
            // Reset to empty if not available
            setTotalProfitandLossAmount({
              SL_No: '',
              Description: '',
              Amount: ''
            });
          }

          dataLoadedRef.current = true;
          showLoading(false);
        } catch (mappingError) {
          console.error('Error mapping API data:', mappingError);
          initializeTableData();
          // Reset totalProfitandLossAmount on error
          setTotalProfitandLossAmount({
            SL_No: '',
            Description: '',
            Amount: ''
          });
          dataLoadedRef.current = true;
          showLoading(false);
        }
      } else {
        // If status is true but data is missing/null, initialize silently (not an error)
        if (response.data && response.data.status) {
          console.log('API returned success status but no data array, initializing with default form');
          initializeTableData();
          // Check for totalProfitandLossAmount even if data array is empty
          if (response.data.totalProfitandLossAmount) {
            const profitLossData = response.data.totalProfitandLossAmount;
            setTotalProfitandLossAmount({
              SL_No: profitLossData.SL_No !== undefined ? profitLossData.SL_No : (profitLossData.sl_No !== undefined ? profitLossData.sl_No : ''),
              Description: profitLossData.Description || profitLossData.description || '',
              Amount: profitLossData.Amount !== undefined && profitLossData.Amount !== null ? profitLossData.Amount.toString() : (profitLossData.amount !== undefined && profitLossData.amount !== null ? profitLossData.amount.toString() : '')
            });
          } else {
            setTotalProfitandLossAmount({
              SL_No: '',
              Description: '',
              Amount: ''
            });
          }
          dataLoadedRef.current = true;
          // No error message - this is a normal case for new/empty records
        } else {
          console.log('Invalid response format or status is false');
          initializeTableData();
          setTotalProfitandLossAmount({
            SL_No: '',
            Description: '',
            Amount: ''
          });
          dataLoadedRef.current = true;
        }
        console.log('Initialized with default data, dataLoadedRef set to true');
      }
    } catch (error) {
      console.error('Error fetching data from API:', error);
      console.error('Error Response:', error.response);
      console.error('Error Status:', error.response?.status);
      console.error('Error Data:', error.response?.data);

      const responseData = error.response?.data;
      if (responseData && responseData.status === true) {
        console.log('API returned status true in catch block, checking data');
        if (Array.isArray(responseData.data) && responseData.data.length > 0) {
          console.log('Data found in catch block, this should not happen');
        }
        initializeTableData();
        setTotalProfitandLossAmount({
          SL_No: '',
          Description: '',
          Amount: ''
        });
      } else if (error.response?.status === 200) {
        console.log('API returned 200 OK, initializing with default data');
        initializeTableData();
        setTotalProfitandLossAmount({
          SL_No: '',
          Description: '',
          Amount: ''
        });
      } else if (error.response?.status === 404) {
        console.log('No existing data found (404), initializing with default data');
        initializeTableData();
        setTotalProfitandLossAmount({
          SL_No: '',
          Description: '',
          Amount: ''
        });
      } else {
        toast.error('Failed to load existing data. Using default form.', messages.COLOR_RED);
        initializeTableData();
        setTotalProfitandLossAmount({
          SL_No: '',
          Description: '',
          Amount: ''
        });
      }
      dataLoadedRef.current = true; // Mark data as loaded even on error
      console.log('Initialized with default data, dataLoadedRef set to true');
    } finally {
      showLoading(false);
    }
  }, []); // Empty dependency array since function doesn't depend on any props or state

  // Memoize the fetch function to prevent recreation
  const fetchHeaderData = useCallback(async () => {
    // Prevent multiple API calls using refs
    if (dataLoadedRef.current || isLoadingRef.current || apiCallMadeRef.current) {
      console.log('Data already loaded, loading in progress, or API call already made - skipping');
      return;
    }

    try {
      const balanceSheetData = localStorage.getItem('balanceSheetFormData');

      if (balanceSheetData) {
        const parsedData = JSON.parse(balanceSheetData);

        const headerInfo = {
          despatchSlNo: parsedData.dslNo || '',
          permanentSlNo: parsedData.pslNo || '',
          companyName: parsedData.nameUnit || '',
          dslNo: parsedData.dslNo || '',
          surveyYear: parsedData.year || ''
        };

        setHeaderData(headerInfo);

        // Fetch existing data if DSL No and Survey Year are available
        if (headerInfo.dslNo && headerInfo.surveyYear) {
          apiCallMadeRef.current = true; // Mark API call as made
          isLoadingRef.current = true;
          await fetchExistingData(headerInfo.dslNo, headerInfo.surveyYear);
          isLoadingRef.current = false;
        } else {
          initializeTableData();
        }
      } else {
        console.log('No balanceSheet data found in localStorage');
        setHeaderData({
          despatchSlNo: 'DSL No. not set',
          permanentSlNo: 'PSL No. not set',
          companyName: 'Company name not set',
          dslNo: '',
          surveyYear: ''
        });
        initializeTableData();
      }
    } catch (error) {
      console.error('Error fetching data from localStorage:', error);
      setHeaderData({
        despatchSlNo: 'Error loading data',
        permanentSlNo: 'Error loading data',
        companyName: 'Error loading data',
        dslNo: '',
        surveyYear: ''
      });
      initializeTableData();
    }
  }, []);

  // Fetch header data from localStorage when component mounts
  useEffect(() => {
    fetchHeaderData();
  }, [fetchHeaderData]); // Only depend on the memoized function

  // Handle click/touch outside to close dropdown (works in production build + touch devices)
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Ignore any outside event for a short window after opening (production can fire mousedown/touchstart in wrong order or twice)
      const now = Date.now();
      if (now - dropdownOpenedAtRef.current < DROPDOWN_OPEN_GUARD_MS) {
        return;
      }
      // Only close if click is truly outside the dropdown wrapper (use .position-relative wrapper that contains input + dropdown)
      const target = event.target;
      if (target && typeof target.closest === 'function' && !target.closest('.position-relative')) {
        closeAllDropdowns();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside, { passive: true });
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, []);

  return (
    <>
      <div className="container-fluid py-3">
        {/* Header Section - Direct Display */}
        <div className="card mb-3">
          <div className="card-body">
            <div className="row align-items-center">
              <div className="col-md-12 mb-3">  
                <h4 className="page_heading mb-3 pb-2 border-bottom">
                  ASI Schedule 2024-25 <span style={{ color: '#d32f2f', fontWeight: 'bold' }}>CONFIDENTIAL <i className="fa fa-lock me-2" style={{ fontSize: '20px', color: '#1976d2' }}></i></span>
                </h4>
                <h5 className="mb-0 pb-3 border-bottom"><span style={{ color: '#1976d2', fontWeight: 'bold' }}>PRODUCTS AND BY-PRODUCTS (manufactured by the unit)</span> </h5>
              </div>
              <div className="col-md-8">
                <div className="row g-3">
                  <div className="col-md-4">
                    <div style={{ fontSize: '14px', fontWeight: '500', color: '#333', marginBottom: '6px' }}>
                      <strong>DSL No.:</strong>
                    </div>
                    <div style={{ fontSize: '14px', color: '#333' }}>
                      {headerData.despatchSlNo}
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div style={{ fontSize: '14px', fontWeight: '500', color: '#333', marginBottom: '6px' }}>
                      <strong>PSL No.:</strong>
                    </div>
                    <div style={{ fontSize: '14px', color: '#333' }}>
                      {headerData.permanentSlNo}
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div style={{ fontSize: '14px', fontWeight: '500', color: '#333', marginBottom: '6px' }}>
                      <strong>Name of Company:</strong>
                    </div>
                    <div style={{ fontSize: '14px', color: '#333' }}>
                      {headerData.companyName}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
       
      

        {/* Main Content Area */}
        <div className="card mb-3">
          <div className="card-body">
            <div className="row">
              <div className="col-12">
                <div className="table-responsive">
                  <table className="table table-bordered mb-0">
                    <thead>
                      <tr>
                        <th>Sl. No.</th>
                        <th>Products/By-products</th>
                        {/* <th>NPC-MS</th> */}
                        <th>Other Item</th>
                        <th>Unit of Quantity</th>
                        <th>Qty. Manufactured</th>
                        <th>Qty. Sold</th>
                        <th>Gross Sale Value (Rs.)</th>
                        <th>Goods and Services tax (GST)</th>
                        <th>Excise Duty/Sales Tax etc.</th>
                        <th>Other Distributive Expenses</th>
                        <th>Subsidy</th>
                        <th>Per Unit Net Sale Value</th>
                      </tr>
                      <tr>
                        <th style={{ textAlign: 'center' }}>(1)</th>
                        <th style={{ textAlign: 'center' }}>(2)</th>
                        <th style={{ textAlign: 'center' }}>(3)</th>
                        <th style={{ textAlign: 'center' }}>(4)</th>
                        <th style={{ textAlign: 'center' }}>(5)</th>
                        <th style={{ textAlign: 'center' }}>(6)</th>
                        <th style={{ textAlign: 'center' }}>(7)</th>
                        <th style={{ textAlign: 'center' }}>(8)</th>
                        <th style={{ textAlign: 'center' }}>(9)</th>
                        <th style={{ textAlign: 'center' }}>(10)</th>
                        <th style={{ textAlign: 'center' }}>(11)</th>
                        <th style={{ textAlign: 'center' }}>(12)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {Array.from({ length: 12 }, (_, i) => {
                        const item = itemData[i];

                        if (!item) {
                          return (
                            <tr key={i}>
                              <td style={{ textAlign: 'center' }}>{i + 1}</td>
                              <td colSpan="10"></td>
                            </tr>
                          );
                        }

                        // Use getFilteredItemsForRow to filter items (already implemented above)

                        return (
                          <tr key={`row-${i}-${item.id}`}>
                            <td style={{ textAlign: 'center' }}>
                              {i + 1}
                            </td>
                            <td>
                              {i < 10 ? (
                                // Searchable dropdown for first 10 rows
                                <div className="position-relative">
                                  <input
                                    type="text"
                                    className="form-control"
                                    value={item.productDesc || ''}
                                    placeholder="Product Description"
                                    style={{ textAlign: 'center', paddingRight: '30px' }}
                                    onChange={(e) => {
                                      handleInputChange(item.id, 'productDesc', e.target.value);
                                      handleSearchChange(e.target.value);
                                    }}
                                    onFocus={() => openDropdown(item.id, 'productDesc')}
                                    onClick={() => openDropdown(item.id, 'productDesc')}
                                    onPointerDown={() => openDropdown(item.id, 'productDesc')}
                                  />
                                  <i className="fa fa-chevron-down position-absolute"
                                    style={{
                                      right: '10px',
                                      top: '50%',
                                      transform: 'translateY(-50%)',
                                      color: '#666',
                                      pointerEvents: 'none'
                                    }}></i>
                                  {dropdownStates[`${item.id}-productDesc`] && (
                                    <div
                                      className="position-absolute w-100 bg-white border rounded shadow-sm"
                                      style={{
                                        top: '100%',
                                        left: 0,
                                        zIndex: 1000,
                                        maxHeight: '200px',
                                        overflowY: 'auto'
                                      }}
                                    >
                                      {(() => {
                                        // Filter out already selected product descriptions for this row
                                        const availableItems = getFilteredProductDescsForRow(item.id, filteredItems);
                                        return availableItems.length > 0 ? (
                                          <>
                                            {availableItems.map((singleItem, index) => (
                                              <div
                                                key={index}
                                                className="px-3 py-2 cursor-pointer hover-bg-light"
                                                style={{ cursor: 'pointer' }}
                                                onClick={() => {
                                                  handleProductDescriptionChange(item.id, singleItem.ITEM_NAME);
                                                  closeDropdown(item.id, 'productDesc');
                                                }}
                                                onMouseEnter={(e) => e.target.style.backgroundColor = '#f8f9fa'}
                                                onMouseLeave={(e) => e.target.style.backgroundColor = 'white'}
                                              >
                                                {singleItem.ITEM_NAME}
                                              </div>
                                            ))}
                                            {hasMoreData && (
                                              <div
                                                className="px-3 py-2 text-center text-muted"
                                                onClick={loadMoreData}
                                                style={{ cursor: 'pointer' }}
                                              >
                                                {isLoadingMore ? 'Loading more...' : 'Load more items'}
                                              </div>
                                            )}
                                          </>
                                        ) : (
                                          <div className="px-3 py-2 text-muted">
                                            {loadingItemMaster ? 'Loading items...' : 'No items found'}
                                          </div>
                                        );
                                      })()}
                                    </div>
                                  )}
                                </div>
                              ) : (
                                // Static text for special rows
                                <input
                                  type="text"
                                  className="form-control"
                                  value={i === 10 ? "Other products/by-products" : i === 11 ? "Values from PL Account for information" : ""}
                                  disabled
                                />
                              )}
                            </td>
                            <td>
                              {i < 10 ? (
                                // Searchable dropdown for first 10 rows
                                <div className="position-relative">
                                  <input
                                    type="text"
                                    className="form-control"
                                    value={item.itemCode || ''}
                                    placeholder="Item Code"
                                    style={{ textAlign: 'center', paddingRight: '30px' }}
                                    maxLength="7"
                                    pattern="[0-9]*"
                                    inputMode="numeric"
                                    onChange={(e) => {
                                      handleInputChange(item.id, 'itemCode', e.target.value);
                                      handleSearchChange(e.target.value);
                                    }}
                                    onFocus={() => openDropdown(item.id, 'itemCode')}
                                    onClick={() => openDropdown(item.id, 'itemCode')}
                                    onPointerDown={() => openDropdown(item.id, 'itemCode')}
                                  />
                                  <i className="fa fa-chevron-down position-absolute"
                                    style={{
                                      right: '10px',
                                      top: '50%',
                                      transform: 'translateY(-50%)',
                                      color: '#666',
                                      pointerEvents: 'none'
                                    }}></i>
                                  {dropdownStates[`${item.id}-itemCode`] && (
                                    <div
                                      className="position-absolute w-100 bg-white border rounded shadow-sm"
                                      style={{
                                        top: '100%',
                                        left: 0,
                                        zIndex: 1000,
                                        maxHeight: '200px',
                                        overflowY: 'auto'
                                      }}
                                    >
                                      {(() => {
                                        // Filter out already selected items for this row
                                        const availableItems = getFilteredItemsForRow(item.id, filteredItems);
                                        return availableItems.length > 0 ? (
                                          <>
                                            {availableItems.map((singleItem, index) => (
                                              <div
                                                key={index}
                                                className="px-3 py-2 cursor-pointer hover-bg-light"
                                                style={{ cursor: 'pointer' }}
                                                onClick={() => {
                                                  handleItemCodeChange(item.id, singleItem.ASICC_CODE);
                                                  closeDropdown(item.id, 'itemCode');
                                                }}
                                                onMouseEnter={(e) => e.target.style.backgroundColor = '#f8f9fa'}
                                                onMouseLeave={(e) => e.target.style.backgroundColor = 'white'}
                                              >
                                                {singleItem.ASICC_CODE}
                                              </div>
                                            ))}
                                            {hasMoreData && (
                                              <div
                                                className="px-3 py-2 text-center text-muted"
                                                onClick={loadMoreData}
                                                style={{ cursor: 'pointer' }}
                                              >
                                                {isLoadingMore ? 'Loading more...' : 'Load more items'}
                                              </div>
                                            )}
                                          </>
                                        ) : (
                                          <div className="px-3 py-2 text-muted">
                                            {loadingItemMaster ? 'Loading items...' : 'No items found'}
                                          </div>
                                        );
                                      })()}
                                    </div>
                                  )}
                                </div>
                              ) : (
                                // Static text for special rows
                                <input
                                  type="text"
                                  className="form-control"
                                  value={i === 10 ? "9921100" : ""}
                                  disabled
                                />
                              )}
                            </td>
                            <td>
                              <input
                                type="text"
                                className="form-control"
                                value={item.unitOfQty || ''}
                                id={`unitOfQty${i}`}
                                name={`unitOfQty${i}`}
                                maxLength="19"
                                disabled={i === 10 || i === 11}
                                onChange={e => handleInputChange(item.id, 'unitOfQty', e.target.value)}
                              />
                        </td>
                            <td>
                              <input
                                type="text"
                                className="form-control"
                                value={item.qtyManufactured || ''}
                                disabled={i === 10 || i === 11}
                                pattern="[0-9]*\.?[0-9]*"
                                inputMode="decimal"
                                maxLength="15"
                                onChange={e => handleInputChange(item.id, 'qtyManufactured', e.target.value)}
                              />
                            </td>
                            <td>
                              <input
                                type="text"
                                className="form-control"
                                value={item.qtySold || ''}
                                disabled={i === 10 || i === 11}
                                pattern="[0-9]*\.?[0-9]*"
                                inputMode="decimal"
                                maxLength="15"
                                onChange={e => handleInputChange(item.id, 'qtySold', e.target.value)}
                              />
                            </td>
                            <td>
                              <input
                                type="text"
                                className="form-control"
                                value={item.grossSaleValue || ''}
                                disabled={i === 11}
                                pattern="[0-9]*\.?[0-9]*"
                                inputMode="decimal"
                                maxLength="15"
                                onChange={e => handleInputChange(item.id, 'grossSaleValue', e.target.value)}
                              />
                            </td>
                            <td>
                              <input
                                type="text"
                                className="form-control"
                                value={item.gst || ''}
                                disabled={i === 11}
                                pattern="[0-9]*\.?[0-9]*"
                                inputMode="decimal"
                                maxLength="15"
                                onChange={e => handleInputChange(item.id, 'gst', e.target.value)}
                              />
                            </td>
                            <td>
                              <input
                                type="text"
                                className="form-control"
                                value={item.exciseDuty || ''}
                                disabled={i === 11}
                                pattern="[0-9]*\.?[0-9]*"
                                inputMode="decimal"
                                maxLength="15"
                                onChange={e => handleInputChange(item.id, 'exciseDuty', e.target.value)}
                              />
                            </td>
                            <td>
                              <input
                                type="text"
                                className="form-control"
                                value={item.otherExpenses || ''}
                                disabled={i === 11}
                                pattern="[0-9]*\.?[0-9]*"
                                inputMode="decimal"
                                maxLength="15"
                                onChange={e => handleInputChange(item.id, 'otherExpenses', e.target.value)}
                              />
                            </td>
                            <td>
                              <input
                                type="text"
                                className="form-control"
                                value={item.subsidy || ''}
                                disabled={i === 11}
                                pattern="[0-9]*\.?[0-9]*"
                                inputMode="decimal"
                                maxLength="15"
                                onChange={e => handleInputChange(item.id, 'subsidy', e.target.value)}
                              />
                            </td>
                            <td>
                              <input
                                type="text"
                                className="form-control"
                                value={item.perUnitNetSaleValue || ''}
                                disabled={true}
                                readOnly
                                style={{ backgroundColor: '#f8f9fa', cursor: 'not-allowed' }}
                                pattern="[0-9]*\.?[0-9]*"
                                inputMode="decimal"
                                maxLength="15"
                              />
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Row 13 - Share (%) of products/by-products directly exported */}
        {/* <div className="card mb-3">
          <div className="card-body">
            <div className="row">
              <div className="col-12">
                <div className="table-responsive">
                  <table className="table table-bordered mb-0">
                    <tbody>
                      <tr>
                        <td style={{ width: '30%', backgroundColor: '#f8f9fa', fontWeight: 'bold', verticalAlign: 'middle', whiteSpace: 'nowrap' }}>
                          13. Share (%) of products/by-products directly exported =
                        </td>
                        <td style={{ width: '70%', padding: '20px' }}>
                          <div className="percentage-input-container">
                            <div className="input-group input-group-lg">
                              <input
                                type="text"
                                className="form-control"
                                value={itemData[12]?.shareExported || ''}
                                placeholder="Enter percentage (e.g., 25.5)"
                                pattern="[0-9]*\.?[0-9]*"
                                inputMode="decimal"
                                maxLength="6"
                                style={{
                                  textAlign: 'center',
                                  fontSize: '18px',
                                  fontWeight: '600',
                                  border: '2px solid #dee2e6',
                                  borderRadius: '8px 0 0 8px',
                                  padding: '20px 20px',
                                  transition: 'all 0.3s ease',
                                  backgroundColor: '#ffffff'
                                }}
                                onChange={e => handleInputChange(12, 'shareExported', e.target.value)}
                              />
                              <span className="input-group-text" style={{
                                backgroundColor: '#f8f9fa',
                                border: '2px solid #dee2e6',
                                borderLeft: 'none',
                                borderRadius: '0 8px 8px 0',
                                fontWeight: 'bold',
                                fontSize: '18px',
                                color: '#495057',
                                padding: '0px 20px',
                                height: 'auto',
                                minHeight: '100%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                boxSizing: 'border-box'
                              }}>
                                %
                              </span>
                            </div>
                            <div className="percentage-info-text">
                              <i className="fa fa-info-circle me-2"></i>
                              Enter the percentage of products/by-products that are directly exported (0-100%)
                            </div>
                          </div>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div> */}

        <div className='card mb-3'>
            <div className='card-header'><h5 className='mb-0 pb-1 pt-1' style={{ fontWeight: 'bold' }}>Control Figures from Profit and Loss</h5></div>
            <div className='card-body'>
             <table className='table table-bordered' style={{minWidth: '100%'}}>
              <thead>
                <tr>
                  <th>SL. No.</th>                 
                  <th>Description</th>
                  <th>Amount</th>
                </tr>
              </thead>
              <tbody>
                {totalProfitandLossAmount.SL_No !== '' || totalProfitandLossAmount.Description !== '' || totalProfitandLossAmount.Amount !== '' ? (
                  <tr>
                    <td>{totalProfitandLossAmount.SL_No !== '' ? totalProfitandLossAmount.SL_No : ''}</td>
                    <td>{totalProfitandLossAmount.Description || ''}</td>
                    <td>{totalProfitandLossAmount.Amount !== '' ? totalProfitandLossAmount.Amount : ''}</td>
                  </tr>
                ) : (
                  <tr>
                    <td colSpan={3} style={{ textAlign: 'center' }}>
                      No data available
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
            </div>

          </div>
           {/* Remarks Section */}
           <div className='row'>
           <div className='col-md-6 remarks-section'>
                      
            <Remarks
              remarksData={remarksData}
              setRemarksData={setRemarksData}
              blockItemData={[]}
              filteredBlockItems={[]}
              isLoadingBlockItems={false}
              debouncedBlockItemSearch={() => {}}
              loadMoreBlockItemData={() => {}}
              hasMoreBlockItems={false}
              isLoadingMoreBlockItems={false}
              selectedItemsFromTable={selectedItemDescriptions}
              remarksList={remarksList}
              availableColumns={[3, 4, 5, 6, 7, 8, 9, 10]}
            />
            </div>
            <div className='col-md-6'>
                <div className='card'>
                    <div className='card-body'>
                        <h6 className='mb-3' style={{ fontWeight: 'bold', color: '#f56565' }}><i className='fa fa-exclamation-triangle me-2'></i>Note</h6>
                        <ul className='list-styled' style={{ fontSize: '14px', color: '#f56565' }}>
                        <li>Col-5, 6, 7, 8, 9 and 10 are Mandatory</li>
                        <li>For OTHERS item, Discription and Col-3 and 4 are also Mandatory</li>
                        </ul>
                    </div>
                </div>
            </div>
      
        
    </div>
        {/* Action Buttons */}
        <div className="row">
          <div className="col-12">
            <hr />
          </div>
          <div className="col-md-8 text-center text-md-start mb-3 mb-md-0">
            <button
              type="button"
              className="btn btn_orange mx-1"
              onClick={() => window.print()}
            >
              Print
              <i className="fa fa-print ms-1"></i>
            </button>
            <button
              type="button"
              className="btn btn_orange mx-1"
              onClick={handleSaveToAPI}
            >
              Save
            </button>
            <button
              type="reset"
              className="btn btn_orange mx-1"
              onClick={handleReset}
            >
              Reset
            </button>
            <button
              type="button"
              className="btn btn_orange mx-1"
              onClick={() => navigate('/asiScheduleMainMenu')}
            >
              Cancel
            </button>
          </div>
          <div className="col-md-4 text-center text-md-end">
            <button
              type="button"
              className="btn btn_orange mx-1"
              onClick={() => navigate('/input-items-imported')}
            >
              <i className="fa fa-angle-left me-1"></i>
              Previous
            </button>

            <button
              type="button"
              className="btn btn_orange mx-1"
              onClick={async () => {
                // Save data first, then navigate only if successful
                const saveSuccess = await handleSaveToAPI();
                if (saveSuccess) {
                  navigate('/ict-usage-energy-conservation');
                }
              }}
            >
              Next
              <i className="fa fa-angle-right ms-1"></i>
            </button>
          </div>
        </div>
        {/* Filter remarks list using centralized function from Custom.js */}
        {(() => {
          const filteredRemarksList = filterRemarksList(remarksList, remarksData);
          return <div className=''>  <Remarks__list remarksData={filteredRemarksList} /> </div>;
        })()}
      </div>
      
      <ToastContainer position="top-right" autoClose={3000} hideProgressBar={false} newestOnTop={false} closeOnClick rtl={false} pauseOnFocusLoss draggable pauseOnHover theme="light" />
    </>
  );
}

export default ProductsAndByProducts; 